import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Textarea } from '@nucleus-commerce/ui/components/textarea';

import { useI18nResolver } from '@/shared/forms/use-i18n-resolver';

import { useStageFormError } from '../hooks/use-stage-form-error';
import { useUpdateStage } from '../hooks/use-workflow-mutations';
import { stageNoteSchema, type StageNoteForm } from '../schemas/stage-forms';

import { ActionDialog } from './action-dialog';
import { MediaMultiPicker } from './media-multi-picker';
import type { StageTarget } from './stage-target';

const KNOWN = new Set(['note', 'fileIds']);

/** A staff note on the stage, files from the library attached to it (CAD renders, casting photos), or both. */
export function StageNoteDialog({
  target,
  onOpenChange,
}: {
  target: StageTarget;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation('production');
  const update = useUpdateStage(target.storeId, target.orderId, 'production:toasts.noteAdded');
  const form = useForm<StageNoteForm>({
    resolver: useI18nResolver(stageNoteSchema),
    defaultValues: { note: '', fileIds: [] },
  });
  const onError = useStageFormError(form.setError, {
    knownFields: KNOWN,
    // `body.fileIds[2]` points at one file; the picker shows the message as a whole.
    mapField: (field) => (field.startsWith('fileIds.') ? 'fileIds' : field),
  });

  const submit = form.handleSubmit(async (values) => {
    form.clearErrors('root.server');
    try {
      await update.mutateAsync({
        key: target.key,
        body: {
          ...(values.note ? { note: values.note } : {}),
          ...(values.fileIds.length > 0 ? { fileIds: values.fileIds } : {}),
        },
      });
      onOpenChange(false);
    } catch (error) {
      onError(error);
    }
  });

  return (
    <Form {...form}>
      <ActionDialog
        open
        wide
        onOpenChange={onOpenChange}
        title={t('note.title', { name: target.name })}
        description={t('note.description')}
        rootError={form.formState.errors.root?.server?.message}
        isPending={update.isPending}
        submitLabel={t('note.submit')}
        submittingLabel={t('note.submitting')}
        onSubmit={submit}
      >
        <FormField
          control={form.control}
          name="note"
          render={({ field }) => (
            <FormItem>
              <FormLabel>{t('note.label')}</FormLabel>
              <FormControl>
                <Textarea {...field} rows={4} maxLength={2000} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="fileIds"
          render={({ field, fieldState }) => (
            <FormItem>
              <FormLabel>{t('note.files')}</FormLabel>
              <MediaMultiPicker
                value={field.value}
                onChange={field.onChange}
                max={20}
                label={t('note.files')}
                invalid={fieldState.invalid}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </ActionDialog>
    </Form>
  );
}
